import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {
  BackofficeClientDetails,
  BackofficeClientDetailsResponse,
  BackofficeClients,
  BackofficeHighPriority,
  BackofficeHighPriorityResponse,
  BackofficeLastTen,
  BackofficeUpcomingVisits,
  Breadcrumb,
  ClientGuestResponse,
  ClientInterestsResponse,
  EditClient,
  ServerObjResponse,
  ServerResponse,
} from '@models';
import { ModalSuccessService } from '@modules';
import { dateUTC } from '@utilities';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BackofficeClientsService {
  public clients = environment.CLIENT_MANAGE;
  public broker = environment.BROKER;

  public breadcrumb: Subject<Breadcrumb[]> = new Subject<Breadcrumb[]>();

  public totalClient: BehaviorSubject<number> = new BehaviorSubject<number>(
    null
  );

  public clientDetails: BehaviorSubject<
    BackofficeClientDetails
  > = new BehaviorSubject<BackofficeClientDetails>(null);

  public clientDetailsStatus: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly http: HttpClient,
    private readonly modalSuccess: ModalSuccessService
  ) {}

  public setBreadcrumb(bread: Breadcrumb[]): void {
    this.breadcrumb.next(bread);
  }

  public setTotalClient(total: number): void {
    this.totalClient.next(total);
  }

  /**
   * Get clients dashboard information
   * @param filter filter (type | status)
   */
  public getDashboard(filter?: string): Observable<BackofficeClients[]> {
    const path = `${this.clients.CLIENTS}${filter || ''}`;
    return this.http.get<ServerResponse>(path).pipe(map((e) => e.response));
  }

  public getHighPriorityClient(
    filter?: string
  ): Observable<BackofficeHighPriority[]> {
    const path = `${this.clients.HIGH_PRIORITY}${filter}`;
    return this.http.get<BackofficeHighPriorityResponse>(path).pipe(
      map((e: BackofficeHighPriorityResponse) => {
        const length = e.response.length >= 10 ? 10 : e.response.length;
        const lastTen = e.response.slice(0, length);
        return lastTen;
      })
    );
  }

  public getLastTenClient(filter?: string): Observable<BackofficeLastTen[]> {
    const path = `${this.clients.LAST_TEN}${filter}`;
    return this.http.get<ServerResponse>(path).pipe(map((e) => e.response));
  }

  /**
   * Get details by client id
   * @param id client id
   */
  public getClientDetails(
    id: string
  ): Observable<BackofficeClientDetailsResponse> {
    const path = `${this.clients.CLIENT}/${id}`;
    return this.http.get<BackofficeClientDetailsResponse>(path);
  }

  public setClientDetails(details: BackofficeClientDetails): void {
    this.clientDetails.next(details);
  }

  /**
   * Get upcoming visits by client id
   * @param id client id
   */
  public getUpcomingVisits(id: string): Observable<ServerObjResponse> {
    const path = this.clients.UPCOMING_VISITS.replace('{clientId}', id);
    return this.http.get<ServerObjResponse>(path).pipe(
      map(({ response }) => {
        const formatDates = (
          e: BackofficeUpcomingVisits
        ): BackofficeUpcomingVisits => {
          return {
            ...e,
            dateTime: dateUTC(e.dateTime),
          };
        };

        return {
          response: response.map((e: BackofficeUpcomingVisits) =>
            formatDates(e)
          ),
        };
      })
    );
  }

  /**
   * Get history visits by client id
   * @param id client id
   */
  public getHistoryVisits(id: string): Observable<ServerObjResponse> {
    const path = this.clients.HISTORY.replace('{clientId}', id);
    return this.http.get<ServerObjResponse>(path).pipe(
      map(({ response }) => {
        const formatDates = (
          e: BackofficeUpcomingVisits
        ): BackofficeUpcomingVisits => {
          return {
            ...e,
            dateTime: dateUTC(e.dateTime),
          };
        };

        return {
          response: response.map((e: BackofficeUpcomingVisits) =>
            formatDates(e)
          ),
        };
      })
    );
  }

  /**
   * Get guests list by client id
   * @param id client id
   */
  public getGuests(id: string): Observable<ClientGuestResponse> {
    const path = this.clients.GUESTS.replace('{clientId}', id);
    return this.http.get<ClientGuestResponse>(path);
  }

  /**
   * Delete a guest
   * @param clientId client id
   * @param guestId guests id
   */
  public deleteGuest(clientId: string, guestId: string): Observable<{}> {
    this.modalSuccess.modal.next(true);
    this.modalSuccess.button.next('Cerrar');

    const path = this.clients.DELETE_GUEST.replace(
      '{clientId}',
      clientId
    ).replace('{guestId}', guestId);

    return this.http.delete(path);
  }

  /**
   * Post get all brokers
   */
  public getBrokers(): Observable<ServerResponse> {
    const path = this.broker.BROKER;
    return this.http.get<ServerResponse>(path);
  }

  /**
   * Update client information
   * @param clientId client id
   * @param data new client data
   */
  public updateClientData(
    clientId: string,
    data: EditClient
  ): Observable<ServerResponse> {
    const path = this.clients.CLIENT;
    this.modalSuccess.modal.next(true);
    this.modalSuccess.button.next('Cerrar');

    return this.http.put<ServerResponse>(`${path}/${clientId}`, data);
  }

  /**
   * Get previous guests list
   * @param clientId client id
   * @param brokerId broker id
   */
  public getPreviousGuests(
    clientId: string,
    brokerId: string
  ): Observable<ServerResponse> {
    const path = this.clients.PREVIOUS_GUESTS.replace(
      '{clientId}',
      clientId
    ).replace('{brokerId}', brokerId);
    return this.http.get<ServerResponse>(path);
  }

  public getClientInterests(
    clientId: string
  ): Observable<ClientInterestsResponse> {
    const path = this.clients.INTERESTS_BY_TYPE;
    return this.http.get<ClientInterestsResponse>(path, {
      params: { clientId },
    });
  }
}
